import type { StyleConfig, StyleOverrideMap } from './StyleConfig';
import { sampleDayCycle } from './DayCycle';
import { sunIntensityByElevation } from './LightingCurves';
import { solveLighting, type DerivedLighting } from './LightingSolver';

export type OverridePath = string;

export type ResolvedStyle = StyleConfig & {
  derivedLighting: DerivedLighting;
};

export const SOLVER_MANAGED_PATHS = new Set<OverridePath>([
  'sunRig.key.colorHex',
  'sunRig.key.intensity',
  'sunRig.fill.colorHex',
  'sunRig.fill.intensity',
  'fog.colorHex',
  'fog.density',
]);

const DERIVED_LIGHTING_OVERRIDE_PREFIX = 'derivedLighting.';

const DERIVED_TO_STYLE_PATH: Partial<Record<keyof DerivedLighting, OverridePath>> = {
  sunColorHex: 'sunRig.key.colorHex',
  sunIntensity: 'sunRig.key.intensity',
  fillIntensity: 'sunRig.fill.intensity',
  hemiSkyHex: 'sunRig.hemisphere.skyColorHex',
  hemiGroundHex: 'sunRig.hemisphere.groundColorHex',
  hemiIntensity: 'sunRig.hemisphere.intensity',
  environmentIntensity: 'sunRig.environmentIntensity',
  fogColorHex: 'fog.colorHex',
  fogDensity: 'fog.density',
  bounceColorHex: 'sunRig.bounce.colorHex',
  rimColorHex: 'sunRig.rim.colorHex',
};

export const MASTER_SUN_DIRECTION_PATHS = new Set<OverridePath>([
  'sky.sunDir',
  'sky.secondarySunDir',
]);

export function resolveStyle(style: StyleConfig): ResolvedStyle {
  const sampled = sampleDayCycle(style.dayCycle);
  const activeMaster = sampled?.master ?? style.master;
  const activeOverrides = stripMasterSunDirectionOverrides(sampled?.overrides ?? style.overrides);
  const resolved = cloneStyle(style) as ResolvedStyle;
  resolved.master = cloneStyle(activeMaster);
  resolved.overrides = cloneStyle(activeOverrides);

  applyEnabledOverrides(resolved, activeOverrides, (path) => path.startsWith('sky.'));
  const derivedOverrides = derivedLightingOverrides(activeOverrides);
  const derived = solveLighting(activeMaster, resolved.sky, derivedOverrides);
  const sunColorHex = overrideValue(activeOverrides, 'sunRig.key.colorHex', derived.sunColorHex) as string;
  const sunIntensity = overrideValue(activeOverrides, 'sunRig.key.intensity', derived.sunIntensity) as number;
  const fillScale = activeMaster.secondarySun.enabled ? activeMaster.secondarySun.intensity : 0;

  setPath(resolved, 'sky.sunDir', vectorToPlain(derived.sunDir));
  setPath(resolved, 'sky.secondarySunDir', vectorToPlain(derived.secondarySunDir));
  setPath(resolved, 'sunRig.key.colorHex', sunColorHex);
  setPath(resolved, 'sunRig.key.intensity', sunIntensity);
  setPath(resolved, 'sunRig.fill.colorHex', derived.hemiSkyHex);
  setPath(resolved, 'sunRig.fill.intensity', sunIntensityByElevation(activeMaster.sunElevationDeg) * 0.35 * fillScale);
  setPath(resolved, 'fog.colorHex', derived.fogColorHex);
  setPath(resolved, 'fog.density', derived.fogDensity);
  applyDerivedLightingOverrides(resolved, activeOverrides, derived);

  applyEnabledOverrides(resolved, activeOverrides);
  resolved.derivedLighting = {
    ...derived,
    sunColorHex,
    sunIntensity,
    fillIntensity: resolved.sunRig.fill.intensity,
    hemiSkyHex: resolved.sunRig.hemisphere.skyColorHex,
    hemiGroundHex: resolved.sunRig.hemisphere.groundColorHex,
    hemiIntensity: resolved.sunRig.hemisphere.intensity,
    environmentIntensity: resolved.sunRig.environmentIntensity,
    bounceColorHex: resolved.sunRig.bounce.colorHex,
    rimColorHex: resolved.sunRig.rim.colorHex,
    fogColorHex: resolved.fog.colorHex,
    fogDensity: resolved.fog.density,
  };
  return resolved;
}

export function isOverridden(style: StyleConfig, path: OverridePath): boolean {
  return Boolean(style.overrides[path]?.enabled);
}

export function setOverride(style: StyleConfig, path: OverridePath, value: unknown): void {
  style.overrides[path] = { enabled: true, value: cloneStyle(value) };
}

export function clearOverride(style: StyleConfig, path: OverridePath): void {
  if (!style.overrides[path]) return;
  style.overrides[path].enabled = false;
}

export function getPath(source: unknown, path: OverridePath): unknown {
  let cursor = source as Record<string, unknown>;
  for (const part of path.split('.')) {
    if (!cursor || typeof cursor !== 'object') return undefined;
    cursor = cursor[part] as Record<string, unknown>;
  }
  return cursor;
}

export function setPath(target: unknown, path: OverridePath, value: unknown): void {
  const parts = path.split('.');
  let cursor = target as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const next = cursor[part];
    if (!next || typeof next !== 'object') cursor[part] = {};
    cursor = cursor[part] as Record<string, unknown>;
  }
  cursor[parts[parts.length - 1]] = cloneStyle(value);
}

function overrideValue(overrides: StyleOverrideMap, path: OverridePath, fallback: unknown): unknown {
  const override = overrides[path];
  return override?.enabled ? override.value : fallback;
}

function applyEnabledOverrides(
  target: StyleConfig,
  overrides: StyleOverrideMap,
  predicate: (path: OverridePath) => boolean = () => true,
): void {
  for (const [path, override] of Object.entries(overrides)) {
    if (MASTER_SUN_DIRECTION_PATHS.has(path)) continue;
    if (path.startsWith(DERIVED_LIGHTING_OVERRIDE_PREFIX)) continue;
    if (!override.enabled || !predicate(path)) continue;
    setPath(target, path, override.value);
  }
}

function derivedLightingOverrides(overrides: StyleOverrideMap): Partial<DerivedLighting> {
  const out: Partial<DerivedLighting> = {};
  for (const [path, override] of Object.entries(overrides)) {
    if (!path.startsWith(DERIVED_LIGHTING_OVERRIDE_PREFIX) || !override.enabled) continue;
    const key = path.slice(DERIVED_LIGHTING_OVERRIDE_PREFIX.length) as keyof DerivedLighting;
    out[key] = override.value as never;
  }
  return out;
}

function applyDerivedLightingOverrides(
  resolved: ResolvedStyle,
  overrides: StyleOverrideMap,
  derived: DerivedLighting,
): void {
  for (const [key, stylePath] of Object.entries(DERIVED_TO_STYLE_PATH) as Array<[keyof DerivedLighting, OverridePath]>) {
    const override = overrides[`${DERIVED_LIGHTING_OVERRIDE_PREFIX}${key}`];
    if (!override?.enabled) continue;
    setPath(resolved, stylePath, derived[key]);
  }
}

function stripMasterSunDirectionOverrides(overrides: StyleOverrideMap): StyleOverrideMap {
  const next = cloneStyle(overrides);
  for (const path of MASTER_SUN_DIRECTION_PATHS) delete next[path];
  return next;
}

function vectorToPlain(vector: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
  return { x: vector.x, y: vector.y, z: vector.z };
}

function cloneStyle<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
